<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 380));

const containerStyle = computed(
  () =>
    ({
      background: props.block.backgroundColor || "#0a0a0a",
      fontFamily:
        props.block.fontFamily ||
        "'Inter', 'SF Pro Display', system-ui, sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.4}px`,
    fontSize: `${base}px`,
    radius: `${props.block.cardRadius ?? 12}px`,
  };
});
</script>

<template>
  <div class="cd-saas" :style="containerStyle">
    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-saas__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 80}ms`,
      }"
    >
      <div
        class="cd-saas__digit"
        :style="{
          fontSize: cardSize.fontSize,
          color: block.digitColor || '#fafafa',
        }"
      >
        <span :key="seg.value">{{ seg.value }}</span>
      </div>
      <div
        class="cd-saas__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || 'rgba(250,250,250,0.5)',
        }"
      >
        {{ seg.label }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-saas {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}
.cd-saas__cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #141414;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.03) inset,
    0 0 0 1px rgba(255, 255, 255, 0.02);
  transition:
    border-color 0.3s ease,
    transform 0.3s ease;
  animation: cd-saas-fade 600ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
.cd-saas__cell:hover {
  border-color: rgba(255, 255, 255, 0.16);
  transform: translateY(-2px);
}
@keyframes cd-saas-fade {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.cd-saas__digit {
  font-weight: 600;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
}
.cd-saas__digit span {
  display: inline-block;
  animation: cd-saas-tick var(--cd-tick, 380ms) cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes cd-saas-tick {
  0% {
    opacity: 0;
    transform: translateY(-6px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
.cd-saas__label {
  margin-top: 8px;
  font-weight: 500;
  letter-spacing: -0.01em;
}
</style>
